/**
 * controls.ts -- Power buttons + console command bar -- Phase 230 (PANEL-01/PANEL-02/PANEL-09,
 * D-06/D-07/D-08). Archive/Restore -- Phase 238 Plan 15 (D-04/D-07/D-08/D-10).
 *
 * Power: start/stop fire the POST immediately; kill/restart require confirming a Bootstrap
 * modal that names the server and states the consequence first (D-07, T-230-26).
 *
 * Command bar: submitting appends an immediate optimistic grey `> command` echo DIRECTLY into
 * the SAME `.mc-console-output` <pre> that McConsole itself writes to (the identical selector
 * console.ts uses at construction -- src/js/mc/console.ts line 196), as an uncoordinated,
 * independent DOM writer -- this file never touches the McConsole class or its WebSocket.
 * The command is POSTed over the REST `handleSendCommand!` signal only (D-01/D-08/T-230-27);
 * the authoritative sent-check/failed-cross ack is rendered on that SAME echo line from the
 * POST's `{ok}` JSON result (T-230-29 -- the echo itself stays grey/provisional). The command's
 * real OUTPUT still arrives later on the already-open read-only WS console stream -- no code
 * here handles that, McConsole already renders it.
 *
 * D-05: both button groups + the command input/send button are disabled server-side (Latte)
 * when the server is suspended; mc.ts re-evaluates this live on every 30s poll tick.
 *
 * Archive/Restore (D-10): Archive requires confirming the SAME shared `#mc-confirm-modal` kill/
 * restart already use (never a second modal element), with the confirm button's class swapped
 * to `btn-warning` (archiving is reversible) instead of the hardcoded-in-Latte `btn-danger`
 * (reserved for Kill's genuine data-loss risk) -- UI-SPEC Interaction Contract §3. Restore fires
 * directly with no modal at all, mirroring the start/stop "safe direction" branch. Both reuse
 * the existing fetch/toast primitives below; a successful Archive or Restore reloads the page
 * rather than attempting a partial re-render, since this file has a documented history of
 * silently diverging from detail.latte (the Phase 235 poll-tick regression) and a reload is the
 * divergence-free option for a state change this large.
 *
 * Bootstrap Modal/Toast imported directly from the 'bootstrap' package -- never read off the
 * global window object (that path is a silent no-op -- feedback_bootstrap_not_on_window).
 *
 * Backups (D-02/D-04, Phase 252): bindBackups() no longer fetches at init -- it now fires its
 * first request the same way the read-only config panels do, off a delegated shown.bs.collapse
 * listener guarded by a fetch-once flag on the panel element (mirrors bindConfigPanels()/
 * loadConfigPanel() below). A collapsed-by-default panel must not issue a request nobody asked
 * for. Once a backup is in progress, scheduleRefresh()'s setTimeout poll loop is page-lifetime,
 * not accordion-lifetime -- collapsing the panel never pauses or cancels it (D-04), so an
 * operator who starts a backup and closes the panel still gets the completion toast without
 * having to keep it open.
 */

import { Modal, Toast } from 'bootstrap';

interface SignalResponse {
    ok?: boolean;
    message?: string;
}

const POWER_VERBS: Record<string, string> = {
    start: 'Start',
    stop: 'Stop',
    restart: 'Restart',
    kill: 'Kill',
};

// D-07: only kill/restart are destructive enough to require the naming+consequence modal.
const POWER_CONSEQUENCES: Record<string, string> = {
    restart: 'Restart stops then immediately starts the server again — active players will be disconnected during the brief downtime.',
    kill: 'Kill immediately terminates the process — unsaved world changes since the last save may be lost.',
};

let inited = false;

/** Idempotent init -- safe to call every time initDetail() runs on this container. */
export function initControls(container: HTMLElement): void {
    if (inited) return;
    inited = true;

    // D-10: archive/restore lookups are container-scoped (never document-wide) and run
    // regardless of whether .mc-console-controls exists on this page -- the fleet page renders
    // many .mc-restore-btn rows inside its Archived accordion with NO .mc-console-controls at
    // all, and the archived detail page renders exactly one .mc-restore-btn in place of the
    // whole console-controls slot (238-10's `{if !isArchived} ... {else}` swap).
    bindArchiveButton(container);
    bindRestoreButtons(container);

    const controlsBar = container.querySelector<HTMLElement>('.mc-console-controls');
    if (!controlsBar) return; // not logged in, or an archived detail page -- markup absent

    bindPowerButtons(container, controlsBar);
    bindCommandForm(container, controlsBar);
    bindBackups(container);
    bindConfigPanels(container);
}

// ---------------------------------------------------------------------------
// Shared confirm modal -- kill/restart (bindPowerButtons) and archive (bindArchiveButton) both
// populate and show this SAME #mc-confirm-modal element; restore never touches it (D-10).
// ---------------------------------------------------------------------------

interface PendingConfirmAction {
    url: string;
    btn: HTMLButtonElement;
    fire: (url: string, btn: HTMLButtonElement) => void;
}

let confirmModalEl: HTMLElement | null = null;
let confirmModal: Modal | null = null;
let pendingConfirm: PendingConfirmAction | null = null;
let confirmBound = false;

/**
 * Resolves the shared #mc-confirm-modal element/instance and binds its single confirm-button
 * click listener EXACTLY once, no matter how many callers (bindPowerButtons, bindArchiveButton)
 * request it in the same page load -- D-10, the modal element is reused, never duplicated, and
 * there is exactly one dispatch point for whichever action is currently pending.
 */
function getConfirmModal(): { el: HTMLElement | null; modal: Modal | null } {
    if (!confirmBound) {
        confirmBound = true;
        confirmModalEl = document.getElementById('mc-confirm-modal');
        confirmModal = confirmModalEl ? new Modal(confirmModalEl) : null;

        confirmModalEl?.querySelector<HTMLButtonElement>('.mc-confirm-modal-confirm')?.addEventListener('click', () => {
            const toFire = pendingConfirm;
            pendingConfirm = null;
            confirmModal?.hide();
            if (toFire) toFire.fire(toFire.url, toFire.btn);
        });
    }
    return { el: confirmModalEl, modal: confirmModal };
}

/**
 * Populate the shared modal's title/consequence/confirm-label AND explicitly set the confirm
 * button's class on EVERY populate (UI-SPEC Interaction Contract §3, T-238-42) -- the Latte
 * markup hardcodes `btn btn-danger`, so relying on that default would let Kill inherit
 * `btn-warning` after a cancelled Archive. `destructive` picks btn-danger (Kill/Restart, genuine
 * data-loss risk) vs btn-warning (Archive, reversible -- D-10).
 */
function populateConfirmModal(
    modalEl: HTMLElement,
    title: string,
    consequence: string,
    confirmLabel: string,
    destructive: boolean,
): void {
    const titleEl = modalEl.querySelector<HTMLElement>('#mc-confirm-modal-label');
    const bodyEl = modalEl.querySelector<HTMLElement>('.mc-confirm-modal-consequence');
    const confirmBtn = modalEl.querySelector<HTMLElement>('.mc-confirm-modal-confirm');
    // textContent only (T-230-30/T-238-10) -- server name is never rendered as HTML.
    if (titleEl) titleEl.textContent = title;
    if (bodyEl) bodyEl.textContent = consequence;
    if (confirmBtn) {
        confirmBtn.textContent = confirmLabel;
        if (destructive) {
            confirmBtn.classList.remove('btn-warning');
            confirmBtn.classList.add('btn-danger');
        } else {
            confirmBtn.classList.remove('btn-danger');
            confirmBtn.classList.add('btn-warning');
        }
    }
}

// ---------------------------------------------------------------------------
// Power buttons (D-07)
// ---------------------------------------------------------------------------

function bindPowerButtons(_container: HTMLElement, controlsBar: HTMLElement): void {
    const serverName = controlsBar.dataset.serverName ?? 'this server';
    const { el: modalEl, modal } = getConfirmModal();

    controlsBar.querySelectorAll<HTMLButtonElement>('.mc-power-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const signal = btn.dataset.signal ?? '';
            const url = btn.dataset.powerUrl ?? '';
            if (!signal || !url) return;

            if (signal === 'restart' || signal === 'kill') {
                if (!modalEl || !modal) return;

                // Kill/Restart are always genuinely destructive -- explicit `true` here (rather
                // than relying on the modal's markup default) is what keeps Kill reading as
                // btn-danger even after a previously-cancelled Archive left btn-warning applied.
                populateConfirmModal(
                    modalEl,
                    `${POWER_VERBS[signal]} ${serverName}?`,
                    POWER_CONSEQUENCES[signal] ?? '',
                    POWER_VERBS[signal],
                    true,
                );
                pendingConfirm = { url, btn, fire: (u, b) => firePower(u, signal, b) };

                modal.show();
                return;
            }

            // start/stop -- non-destructive, fire directly with no modal (D-07).
            firePower(url, signal, btn);
        });
    });
}

function firePower(url: string, signal: string, btn: HTMLButtonElement): void {
    btn.disabled = true;

    const body = new URLSearchParams();
    body.set('signal', signal);

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
    })
        .then((r) => r.json())
        .then((data: SignalResponse) => {
            btn.disabled = false;
            const verb = POWER_VERBS[signal] ?? signal;
            if (data.ok === true) {
                showToast(`${verb} sent.`, 'success');
            } else {
                showToast(data.message ?? `${verb} failed.`, 'error');
            }
        })
        .catch(() => {
            btn.disabled = false;
            showToast(`${POWER_VERBS[signal] ?? signal} failed.`, 'error');
        });
}

// ---------------------------------------------------------------------------
// Archive / Restore (D-04/D-07/D-08/D-10 -- Phase 238 Plan 15)
//
// Archive requires confirming the shared #mc-confirm-modal (never a second modal); Restore is
// the safe direction (D-10) and fires directly, exactly like the start/stop branch above. Both
// buttons are looked up via a container-scoped querySelectorAll (never document-wide) -- the
// fleet page's Archived accordion can render many .mc-restore-btn rows while the detail page
// renders at most one of either button.
// ---------------------------------------------------------------------------

function bindArchiveButton(container: HTMLElement): void {
    const { el: modalEl, modal } = getConfirmModal();

    container.querySelectorAll<HTMLButtonElement>('.mc-archive-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const url = btn.dataset.archiveUrl ?? '';
            const serverName = btn.dataset.serverName ?? 'this server';
            if (!url || !modalEl || !modal) return;

            // D-04: the ONE place the retroactive-shrink consequence is disclosed before the
            // operator commits -- shipped verbatim, not shortened.
            populateConfirmModal(
                modalEl,
                `Archive ${serverName}?`,
                `Archiving hides ${serverName} from the fleet grid, leaderboards, activity feed, heatmap and every fleet-wide total — including past totals, which will shrink to no longer include its history. No data is deleted. You can restore it at any time from the Archived section on the fleet page.`,
                'Archive',
                false, // reversible -- reads as btn-warning, never btn-danger (D-10)
            );
            pendingConfirm = {
                url,
                btn,
                fire: (u, b) => fireRosterAction(u, b, `${serverName} archived.`, 'Archive failed.'),
            };

            modal.show();
        });
    });
}

function bindRestoreButtons(container: HTMLElement): void {
    container.querySelectorAll<HTMLButtonElement>('.mc-restore-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const url = btn.dataset.restoreUrl ?? '';
            const serverName = btn.dataset.serverName ?? 'this server';
            if (!url) return;

            // D-10 -- no modal, fires directly, exactly like start/stop.
            fireRosterAction(url, btn, `${serverName} restored.`, 'Restore failed.');
        });
    });
}

/**
 * Shared POST handler for Archive/Restore -- same fetch shape firePower() uses (form-encoded,
 * X-Requested-With, disable-while-in-flight, server-provided message takes precedence on
 * failure) with no extra body param (the archive/restore signal needs nothing beyond the URL
 * itself, unlike power's shared endpoint dispatched via a `signal` param). On success the page
 * reloads rather than attempting a partial client-side re-render (see module header) -- the
 * operating page's controls, fleet-grid membership and Archived-accordion membership all change
 * at once, and this file has a documented history of silently diverging from detail.latte (the
 * Phase 235 poll-tick regression) when it tried to hand-patch state like this.
 */
function fireRosterAction(url: string, btn: HTMLButtonElement, successMsg: string, failMsg: string): void {
    btn.disabled = true;

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: '',
    })
        .then((r) => r.json())
        .then((data: SignalResponse) => {
            if (data.ok === true) {
                showToast(successMsg, 'success');
                window.location.reload();
            } else {
                btn.disabled = false;
                showToast(data.message ?? failMsg, 'error');
            }
        })
        .catch(() => {
            btn.disabled = false;
            showToast(failMsg, 'error');
        });
}

// ---------------------------------------------------------------------------
// Command bar (D-08)
// ---------------------------------------------------------------------------

function bindCommandForm(container: HTMLElement, controlsBar: HTMLElement): void {
    const form = controlsBar.querySelector<HTMLFormElement>('.mc-command-form');
    const input = controlsBar.querySelector<HTMLInputElement>('.mc-command-input');
    if (!form || !input) return;

    form.addEventListener('submit', (e) => {
        e.preventDefault();

        const command = input.value.trim();
        const commandUrl = form.dataset.commandUrl ?? '';
        if (!command || !commandUrl) return;

        input.value = '';

        // D-08: immediate optimistic grey echo, appended directly into the same
        // .mc-console-output McConsole itself writes to (uncoordinated -- no WS send here).
        const outputEl = container.querySelector<HTMLPreElement>('.mc-console-output');
        const ackEl = outputEl ? appendCommandEcho(outputEl, command) : null;

        postCommand(commandUrl, command, ackEl);
    });
}

/**
 * Append a grey `> command` echo line into the console output, returning the empty ack <span>
 * so the caller can fill it in once the REST POST resolves.
 */
function appendCommandEcho(outputEl: HTMLPreElement, command: string): HTMLElement {
    const line = document.createElement('span');
    line.className = 'mc-console-echo';
    line.style.opacity = '0.6';
    // textContent-equivalent node -- command text is never interpolated as HTML (T-230-30).
    line.appendChild(document.createTextNode(`> ${command}`));

    const ack = document.createElement('span');
    ack.className = 'mc-console-echo-ack';
    line.appendChild(ack);

    line.appendChild(document.createTextNode('\n'));
    outputEl.appendChild(line);
    outputEl.scrollTop = outputEl.scrollHeight;

    return ack;
}

function postCommand(url: string, command: string, ackEl: HTMLElement | null): void {
    const body = new URLSearchParams();
    body.set('command', command);

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
    })
        .then((r) => r.json())
        .then((data: SignalResponse) => {
            const ok = data.ok === true;
            setAck(ackEl, ok);
            if (!ok) {
                showToast(data.message ?? 'Command failed.', 'error');
            }
        })
        .catch(() => {
            setAck(ackEl, false);
            showToast('Command failed.', 'error');
        });
}

/** T-230-29: the authoritative sent/failed ack, set from the POST's {ok} result -- never the echo itself. */
function setAck(ackEl: HTMLElement | null, ok: boolean): void {
    if (!ackEl) return;
    ackEl.textContent = ok ? ' ✓' : ' ✗'; // textContent only (T-230-30)
    ackEl.classList.add(ok ? 'text-success' : 'text-danger');
}

// ---------------------------------------------------------------------------
// Toast (shared by power + command paths)
// ---------------------------------------------------------------------------

export function showToast(msg: string, type: 'success' | 'error'): void {
    const toastEl = document.getElementById('mc-controls-toast');
    if (!toastEl) return;
    toastEl.classList.remove('bg-success', 'bg-danger', 'text-white');
    toastEl.classList.add(type === 'success' ? 'bg-success' : 'bg-danger', 'text-white');
    const msgEl = toastEl.querySelector<HTMLElement>('.mc-controls-toast-msg');
    if (msgEl) msgEl.textContent = msg;
    new Toast(toastEl, { delay: 3000 }).show();
}

// ---------------------------------------------------------------------------
// Backups -- create / poll-while-in-progress / download-302 (D-11, D-12, PANEL-04)
//
// bindBackups() reads its three signal URLs straight off detail.latte's server-generated
// {link} attributes on .mc-backups-section -- data-backups-url (handleGetBackups!),
// data-create-backup-url (handleCreateBackup!), data-download-backup-url
// (handleDownloadBackup!) -- never a hand-built ?do= URL. That .mc-backups-section div now sits
// nested inside the .mc-backups-panel accordion item (Phase 252 D-01), and its first fetch is
// triggered by that panel's first expand rather than at bindBackups() init time (D-02).
// ---------------------------------------------------------------------------

interface BackupRow {
    uuid: string | null;
    name: string | null;
    bytes: number;
    created_at: string | null;
    completed_at: string | null;
    is_successful: boolean | null;
    status: string; // 'creating' | 'done' | 'failed' -- mirrors McPresenter::backupStatus()
}

interface BackupsResponse extends SignalResponse {
    backups?: BackupRow[];
    anyInProgress?: boolean;
    suspended?: boolean;
    note?: string;
}

const BACKUP_POLL_INTERVAL = 5_000;

/** POST fetch with the codebase's established empty-body shape (matches firePower/postCommand). */
function postFetch(url: string, body: string): Promise<Response> {
    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body,
    });
}

function bindBackups(container: HTMLElement): void {
    const section = container.querySelector<HTMLElement>('.mc-backups-section');
    if (!section) return; // not logged in -- markup doesn't exist on the page

    // Phase 252 D-01: .mc-backups-section always ships nested inside the .mc-backups-panel
    // accordion-item now -- a missing wrapper here is a real markup defect, not a degrade case.
    const panel = section.closest<HTMLElement>('.mc-backups-panel');
    if (!panel) return;

    const backupsUrl = section.dataset.backupsUrl ?? '';
    const createUrl = section.dataset.createBackupUrl ?? '';
    const downloadUrl = section.dataset.downloadBackupUrl ?? '';
    const listEl = section.querySelector<HTMLElement>('.mc-backups-list');
    const createBtn = section.querySelector<HTMLButtonElement>('.mc-create-backup-btn');

    // Suspended servers render no table/list at all (Task 1) -- nothing to fetch or poll.
    if (!listEl || !backupsUrl) return;

    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleRefresh = (): void => {
        if (pollTimer !== null) return; // already scheduled -- never stack overlapping polls
        pollTimer = setTimeout(() => {
            pollTimer = null;
            void loadBackups();
        }, BACKUP_POLL_INTERVAL);
    };

    const loadBackups = async (): Promise<void> => {
        try {
            const res = await postFetch(backupsUrl, '');
            const data: BackupsResponse = await res.json();
            renderBackups(listEl, data, downloadUrl);
            // D-11: poll ONLY while a backup is still creating; the moment the list settles
            // (anyInProgress false/absent) the loop stops itself -- never a perpetual request.
            if (data.ok === true && data.anyInProgress === true) {
                scheduleRefresh();
            }
        } catch {
            panel.dataset.loaded = ''; // allow a retry on the next expand
            appendBackupsMessage(listEl, 'Could not load backups.');
        }
    };

    createBtn?.addEventListener('click', () => {
        if (!createUrl) return;
        createBtn.disabled = true;
        postFetch(createUrl, '')
            .then((r) => r.json())
            .then((data: SignalResponse) => {
                createBtn.disabled = false;
                if (data.ok === true) {
                    // Pterodactyl's own detail string on failure (D-11) -- Ptero's own success
                    // path carries no message, so the toast copy here is ours.
                    showToast('Backup started.', 'success');
                    void loadBackups(); // the new "creating" row kicks the poll loop off itself
                } else {
                    showToast(data.message ?? 'Backup could not be created.', 'error');
                }
            })
            .catch(() => {
                createBtn.disabled = false;
                showToast('Backup could not be created.', 'error');
            });
    });

    // D-02: fetch-once-on-first-expand, mirroring bindConfigPanels()/loadConfigPanel() below --
    // no eager load at init, a collapsed-by-default panel must not fire a request nobody asked
    // for. D-04: no hidden.bs.collapse listener here on purpose, and pollTimer above is never
    // cleared on collapse -- once a backup is in progress the setTimeout poll loop is
    // page-lifetime, not accordion-lifetime, so it keeps running and still fires its completion
    // toast even if the operator collapses this panel before the backup finishes. Do not "fix"
    // that by pausing it here.
    container.addEventListener('shown.bs.collapse', (e: Event) => {
        const collapseEl = e.target as HTMLElement;
        if (collapseEl.closest<HTMLElement>('.mc-backups-panel') !== panel) return;
        if (panel.dataset.loaded === '1') return; // fetch-once guard
        panel.dataset.loaded = '1'; // set BEFORE await -- rapid expand/collapse can't double-fire
        void loadBackups();
    });
}

function renderBackups(listEl: HTMLElement, data: BackupsResponse, downloadUrl: string): void {
    listEl.innerHTML = '';

    if (data.ok !== true) {
        appendBackupsMessage(listEl, data.message ?? 'Could not load backups.');
        return;
    }
    if (data.note === 'unavailable') {
        appendBackupsMessage(listEl, 'Backups unavailable.');
        return;
    }

    const backups = data.backups ?? [];
    if (backups.length === 0) {
        appendBackupsMessage(listEl, 'No backups yet.');
        return;
    }

    backups.forEach((backup) => {
        listEl.appendChild(buildBackupRow(backup, downloadUrl));
    });
}

function buildBackupRow(backup: BackupRow, downloadUrl: string): HTMLElement {
    const row = document.createElement('tr');

    const nameCell = document.createElement('td');
    nameCell.textContent = backup.name ?? '(unnamed)'; // textContent only -- values as-is, T-230-34
    row.appendChild(nameCell);

    const createdCell = document.createElement('td');
    createdCell.className = 'small text-muted';
    createdCell.textContent = formatBackupDate(backup.created_at);
    row.appendChild(createdCell);

    const sizeCell = document.createElement('td');
    sizeCell.className = 'small text-muted';
    sizeCell.textContent = fmtBytes(backup.bytes ?? 0);
    row.appendChild(sizeCell);

    const statusCell = document.createElement('td');
    statusCell.appendChild(buildBackupStatusBadge(backup.status));
    row.appendChild(statusCell);

    const actionCell = document.createElement('td');
    actionCell.className = 'text-end';
    // D-11 false-green-trap guard: ONLY a `done` row (server-computed by backupStatus() --
    // completed_at set AND is_successful true) gets a Download control. A completed-but-FAILED
    // backup (status 'failed') must NEVER be downloadable.
    if (backup.status === 'done' && backup.uuid) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn-sm btn-outline-secondary mc-download-backup-btn';
        btn.textContent = 'Download';
        const uuid = backup.uuid;
        btn.addEventListener('click', () => triggerBackupDownload(downloadUrl, uuid));
        actionCell.appendChild(btn);
    }
    row.appendChild(actionCell);

    return row;
}

function buildBackupStatusBadge(status: string): HTMLElement {
    const span = document.createElement('span');
    if (status === 'done') {
        span.className = 'badge bg-success';
        span.textContent = 'Ready';
    } else if (status === 'failed') {
        span.className = 'badge bg-danger';
        span.textContent = 'Failed';
    } else {
        span.className = 'badge bg-secondary';
        span.textContent = 'Creating…';
    }
    return span;
}

function appendBackupsMessage(listEl: HTMLElement, message: string): void {
    listEl.innerHTML = '';
    const row = document.createElement('tr');
    const cell = document.createElement('td');
    cell.colSpan = 5;
    cell.className = 'text-muted small';
    cell.textContent = message;
    row.appendChild(cell);
    listEl.appendChild(row);
}

function formatBackupDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// Defined locally rather than shared/imported -- mirrors console.ts's own fmtBytes() copy
// ("defined here to avoid cross-import from mc.ts").
function fmtBytes(bytes: number): string {
    if (bytes >= 1_073_741_824) return (bytes / 1_073_741_824).toFixed(1) + ' GB';
    if (bytes >= 1_048_576) return (bytes / 1_048_576).toFixed(0) + ' MB';
    return bytes + ' B';
}

/**
 * D-12: navigate the browser to the signed backup-download URL via a hidden form POST --
 * NEVER fetch(). handleDownloadBackup! is POST-only (requirePost()); a plain <a href>/
 * window.location navigation is a GET and would hit its 405 branch instead of the 302. A
 * hidden-form submit is a genuine browser navigation (not XHR/fetch), fired synchronously
 * from the button's own click handler (preserving the user-gesture context so popup blockers
 * don't interfere), and carries the session cookie same as any other same-origin navigation --
 * the browser then follows the resulting 302 straight to the one-time signed node URL. Opens
 * in a new tab (never unloads the console/detail page) with rel=noopener since the redirect
 * target is a different origin (the Pterodactyl panel host).
 */
function triggerBackupDownload(baseUrl: string, uuid: string): void {
    if (!baseUrl) return;
    const sep = baseUrl.includes('?') ? '&' : '?';
    const url = `${baseUrl}${sep}uuid=${encodeURIComponent(uuid)}`;

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.target = '_blank';
    form.setAttribute('rel', 'noopener');
    form.style.display = 'none';
    document.body.appendChild(form);
    form.submit();
    form.remove();
}

// ---------------------------------------------------------------------------
// Config panels: Schedules / Startup variables / Network allocations (D-13, PANEL-05/06)
//
// configPanelUrl() reads each panel's server-generated {link} URL straight off detail.latte's
// .mc-config-panel data attributes -- data-schedules-url (handleGetSchedules!), data-startup-url
// (handleGetStartup!), data-allocations-url (handleGetAllocations!).
// ---------------------------------------------------------------------------

type ConfigPanelKind = 'schedules' | 'startup' | 'allocations';

interface ConfigResponse extends SignalResponse {
    items?: Array<Record<string, unknown>>;
    suspended?: boolean;
    note?: string;
}

/**
 * One delegated shown.bs.collapse listener covers all three panels (mirrors stardew.ts's
 * "ONE handler covers ALL deferred accordions" contract) -- fetch-once-on-first-expand, never
 * on page load.
 */
function bindConfigPanels(container: HTMLElement): void {
    container.addEventListener('shown.bs.collapse', (e: Event) => {
        const collapseEl = e.target as HTMLElement;
        const panel = collapseEl.closest<HTMLElement>('.mc-config-panel');
        if (!panel) return;
        void loadConfigPanel(panel, collapseEl);
    });
}

function configPanelKind(panel: HTMLElement): ConfigPanelKind | null {
    if (panel.dataset.schedulesUrl) return 'schedules';
    if (panel.dataset.startupUrl) return 'startup';
    if (panel.dataset.allocationsUrl) return 'allocations';
    return null;
}

function configPanelUrl(panel: HTMLElement, kind: ConfigPanelKind): string {
    if (kind === 'schedules') return panel.dataset.schedulesUrl ?? '';
    if (kind === 'startup') return panel.dataset.startupUrl ?? '';
    return panel.dataset.allocationsUrl ?? '';
}

async function loadConfigPanel(panel: HTMLElement, collapseEl: HTMLElement): Promise<void> {
    if (panel.dataset.loaded === '1') return; // fetch-once guard

    const kind = configPanelKind(panel);
    if (!kind) return;
    const url = configPanelUrl(panel, kind);
    if (!url) return;

    const body = collapseEl.querySelector<HTMLElement>('.mc-config-body');
    if (!body) return;

    // Suspended servers render the static note (Task 1) with no .mc-config-placeholder --
    // nothing to fetch, the degrade is already shown.
    if (!body.querySelector('.mc-config-placeholder')) return;

    panel.dataset.loaded = '1'; // set BEFORE await -- rapid expand/collapse can't double-fire

    try {
        const res = await postFetch(url, '');
        const data: ConfigResponse = await res.json();
        renderConfigPanel(body, kind, data);
    } catch {
        panel.dataset.loaded = ''; // allow a retry on the next expand
        renderConfigMessage(body, 'Could not load -- try expanding again.');
    }
}

function renderConfigPanel(body: HTMLElement, kind: ConfigPanelKind, data: ConfigResponse): void {
    if (data.ok !== true) {
        renderConfigMessage(body, data.message ?? 'Could not load.');
        return;
    }
    if (data.note === 'unavailable') {
        renderConfigMessage(body, 'Unavailable while this server is suspended.');
        return;
    }

    const items = data.items ?? [];
    if (items.length === 0) {
        renderConfigMessage(body, 'None configured.');
        return;
    }

    body.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.className = 'table-responsive';
    const table = document.createElement('table');
    table.className = 'table table-sm mb-0';
    const tbody = document.createElement('tbody');

    items.forEach((item) => {
        tbody.appendChild(buildConfigRow(kind, item));
    });

    table.appendChild(tbody);
    wrapper.appendChild(table);
    body.appendChild(wrapper);
}

function renderConfigMessage(body: HTMLElement, message: string): void {
    body.innerHTML = '';
    const p = document.createElement('p');
    p.className = 'text-muted small mb-0';
    p.textContent = message;
    body.appendChild(p);
}

/**
 * D-13: values as-is, no masking -- every field rendered via textContent only (T-230-34), never
 * innerHTML, so a modded/namespaced identifier or a startup var's server_value can never break
 * out of the DOM as markup.
 */
function buildConfigRow(kind: ConfigPanelKind, item: Record<string, unknown>): HTMLElement {
    const row = document.createElement('tr');

    const addCell = (value: unknown): void => {
        const cell = document.createElement('td');
        cell.className = 'small';
        cell.textContent = value === null || value === undefined || value === '' ? '—' : String(value);
        row.appendChild(cell);
    };

    if (kind === 'schedules') {
        addCell(item.name);
        addCell(item.is_active ? 'Active' : 'Inactive');
        addCell(item.next_run_at);
    } else if (kind === 'startup') {
        addCell(item.name);
        addCell(item.env_variable);
        addCell(item.server_value ?? item.default_value);
    } else {
        addCell(item.ip);
        addCell(item.port);
        addCell(item.notes);
    }

    return row;
}
